import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PatientProfileSummary from './components/PatientProfileSummary';
import QuotationSummary from './components/QuotationSummary';
import MedicalDocuments from './components/MedicalDocuments';
import FinalConfirmation from './components/FinalConfirmation';
import AdmissionSkeleton from './AdmissionSkeleton';
import './style.css';
import { useGet } from '@/api/hooks/useGet';
import { GetAdmissionDetails, SubmitAdmission } from '@/api/endpoints/Requests';
import ActionBtn from '@/components/ActionBtn';
import moment from 'moment';
import { Icon } from '@iconify/react/dist/iconify.js';
import { errorToast, successToast } from '@/utils/toasters';
import { Block, CRRender } from '@/components/crRender';

const Admission = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const requestId = state?.requestId;

    const { result: admissionDetails, isPending } = useGet({
        endPoint: GetAdmissionDetails,
        depArr: [],
        allowFetch: !!requestId,
        params: [requestId]
    });

    const handleBack = () => {
        navigate('/panel/provider/requests/medical');
    };

    const handleSubmit = () => {
        setLoading(true);
        SubmitAdmission(requestId).then((res) => {
            successToast("Admission submitted successfully");
            navigate('/panel/provider/requests/medical');
            setLoading(false);
        }).catch((err) => {
            errorToast("Error submitting admission");
            setLoading(false);
        });
    };

    return (
        <>
            {isPending ? (
                <AdmissionSkeleton />
            ) : (
                <div className="admission-container">
                    {/* Patient Identity Header */}
                    <div className="admission-patient-header">
                        <div className="admission-patient-header-left">
                            <div className="admission-patient-identity-badge">
                                <span className="admission-patient-identity-text">{t('patient-identity-token')}</span>
                                <span className="admission-verified-badge">{t('verified')}</span>
                            </div>
                            <h1 className="admission-patient-name-title">{admissionDetails?.patientData?.name}</h1>
                            <div className="admission-patient-meta">
                                <span className="admission-patient-meta-item">
                                    <Icon className="admission-patient-meta-item-icon" icon="iwwa:year" width="18" height="18" />
                                    {admissionDetails?.patientData?.age}
                                </span>
                                <div className="admission-group">
                                    <span className="admission-patient-meta-item">
                                        <Icon className="admission-patient-meta-item-icon" icon="ant-design:number-outlined" width="18" height="18" />
                                        {state?.requestId}
                                    </span>
                                </div>
                                <div className="admission-group">
                                    <span className="admission-patient-meta-item">
                                        <Icon className="admission-patient-meta-item-icon" icon="icons8:gender" width="18" height="18" />
                                        {admissionDetails?.patientData?.gender?.toUpperCase()}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="admission-patient-header-right">
                            <div className="admission-status-badge-orange">
                                {admissionDetails?.requestData?.orderStatus}
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="admission-content">
                        <div className="admission-content-left">
                            <PatientProfileSummary admissionDetails={admissionDetails} />
                            <QuotationSummary admissionDetails={admissionDetails} />
                        </div>

                        <div className="admission-content-right">
                            <MedicalDocuments admissionDetails={admissionDetails} />
                            <FinalConfirmation onSubmit={handleSubmit} isLoading={isPending} />
                        </div>
                    </div>
                    <Block isRender={!state?.isPayment} className="admission-footer-submission">
                        <div onClick={handleBack} className="admission-footer-submission-back-btn">
                            <Icon icon={"fluent-mdl2:back"} />
                            {t("Back to Selection")}
                        </div>
                        <div className="admission-footer-submission-right">
                            <div className="admission-footer-submission-time-container">
                                <span>{t("entry-time")}</span>
                                <div className="admission-footer-submission-time">{`${moment().format("MMMM  DD , YYYY")} . ${moment().format("hh : mm a").toUpperCase()}`}</div>
                            </div>
                            <br />
                            <ActionBtn className="admission-footer-submission-btn" icon={"mdi:motion-play"} onApply={handleSubmit} title={loading ? "loading" : "Admission"} />
                        </div>
                    </Block>
                </div>
            )}
        </>
    );
};

export default Admission;

